import Ionicons from '@expo/vector-icons/Ionicons';
import { router, useFocusEffect, useLocalSearchParams } from 'expo-router';
import { debounce, isEmpty } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { StyleSheet, TouchableOpacity, View } from 'react-native';
import { FlatList, RefreshControl } from 'react-native-gesture-handler';
import { Divider } from 'react-native-paper';
import Animated, { LinearTransition, SlideInRight, SlideOutLeft, SlideOutRight } from 'react-native-reanimated';

import {
  useGetIndividualNpoChatRooms,
  useGetNpoDonationsChatRooms,
  useGetVolunteerEventChatRooms
} from '@/api/hooks/chatRoomsHooks';
import { ChatRoom } from '@/api/models/chatRoomModels';
import DotsLoader from '@/components/DotsLoader';
import TextInputField from '@/components/inputs/TextInputField';
import ListEmptyComponent from '@/components/ListEmptyComponent';
import ChatRoomCard from '@/components/messages/ChatRoomCard';
import { useAuth } from '@/contexts/authContext';
import { useColor } from '@/hooks/useColor';
import { i18n } from '@/utils/i18n';

const MessagesList = () => {
  const { roomId } = useLocalSearchParams<{ roomId?: string }>();

  const colors = useColor();
  const { state, isBrowseOnlyUser } = useAuth();

  const [isSearching, setIsSearching] = useState(true);
  const [filteredChatRooms, setFilteredChatRooms] = useState<ChatRoom[] | undefined>(undefined);

  const defaultValues = { search: '' };

  const {
    data: volunteerEventsChatRooms,
    isPending: eventsPending,
    refetch: refetchEvents
  } = useGetVolunteerEventChatRooms();

  const {
    data: individualNpoChatRooms,
    isPending: individualNpoPending,
    refetch: refetchIndividualNpo
  } = useGetIndividualNpoChatRooms();

  const {
    data: npoDonationsChatRooms,
    isPending: npoDonationsPending,
    refetch: refetchNpoDonation
  } = useGetNpoDonationsChatRooms();

  const { control, watch, setValue } = useForm<{ search?: string }>({
    defaultValues: defaultValues
  });

  const searchWatched = watch('search');

  const renderDivider = () => <Divider style={styles.divider} />;

  // ToDO: IMPROVE THIS MAP (BACKEND COULD SEND SAME DTO)
  const parsedIndividualNpoChatRooms: ChatRoom[] | undefined = individualNpoChatRooms?.map((room) => ({
    id: `${room.subscriberUsers.at(0)?.id}#${room.npos.at(0)?.id}`,
    title:
      state.user?.user?.role === 'INDIVIDUAL'
        ? (room.npos.at(0)?.name ?? '-') //NPO
        : (room.subscriberUsers.at(0)?.fullName ?? '-'), //USER
    subtitle: i18n.t('messages.private_chat'),
    sender:
      state.user?.user?.role === 'INDIVIDUAL'
        ? (room.subscriberUsers.at(0)?.fullName ?? '-') //USER
        : (room.npos.at(0)?.name ?? '-'), //NPO,,
    receiver:
      state.user?.user?.role === 'INDIVIDUAL'
        ? (room.npos.at(0)?.name ?? '-') //NPO
        : (room.subscriberUsers.at(0)?.fullName ?? '-'), //USER,
    footer: '',
    thumbnail:
      state.user?.user?.role === 'INDIVIDUAL'
        ? room.npos.at(0)?.thumbnail
        : room.subscriberUsers.at(0)?.thumbnail !== null && room.subscriberUsers.at(0)?.thumbnail !== undefined
          ? JSON.stringify([room.subscriberUsers.at(0)?.thumbnail])
          : undefined,
    type: 'INDIVIDUALNPO',
    enabled: room.subscriberUsers.length > 0,
    nonProfitOrgId: room.npos.at(0)?.id,
    individualId: room.subscriberUsers.at(0)?.id,
    nonProfitThumb: room.npos.at(0)?.thumbnail,
    individualThumb: room.subscriberUsers.at(0)?.thumbnail
  }));

  const parsedVolunteerEventsChatRooms: ChatRoom[] | undefined = volunteerEventsChatRooms?.map((room) => ({
    id: room.id,
    title: room.npos?.name ?? '-',
    subtitle: `${room.name} - ${i18n.t('mix.volunteer_event')}`,
    footer: `${room._count?.subscribedUsers ?? '0'} users`,
    sender: room.npos?.name ?? '-',
    receiver: `${room._count?.subscribedUsers ?? '0'} users`,
    thumbnail: room.thumbnail,
    type: 'VOLUNTEEREVENT',
    enabled: room._count?.subscribedUsers !== undefined && room._count?.subscribedUsers > 0,
    nonProfitOrgId: room.npos?.id
  }));

  const parsedNpoDonationsChatRooms: ChatRoom[] | undefined = npoDonationsChatRooms?.map((room) => ({
    id: room.npos.at(0)?.id ?? '-',
    title:
      state.user?.user?.role === 'INDIVIDUAL'
        ? `${room.npos.at(0)?.name ?? '-'} Donations` //NPO DONATIONS
        : `Donations`,
    subtitle:
      state.user?.user?.role === 'INDIVIDUAL'
        ? `Messages to donators` //USER
        : (room.npos.at(0)?.name ?? '-'), //NPO
    footer: `${room.subscriberUsers.length ?? '0'} users`,
    sender:
      state.user?.user?.role === 'INDIVIDUAL'
        ? `Messages to donators` //USER
        : (room.npos.at(0)?.name ?? '-'), //NPO,
    receiver:
      state.user?.user?.role === 'INDIVIDUAL'
        ? `${room.npos.at(0)?.name ?? '-'} Donations` //NPO DONATIONS
        : `Donations`,
    thumbnail: room.npos.at(0)?.thumbnail,
    type: 'NPODONATIONS',
    enabled: room.subscriberUsers.length > 0,
    nonProfitOrgId: room.npos.at(0)?.id
  }));

  const chats = parsedNpoDonationsChatRooms
    ?.concat(parsedVolunteerEventsChatRooms ?? [])
    .concat(parsedIndividualNpoChatRooms ?? [])
    .flatMap((room) => room);

  const onRefresh = () => {
    refetchEvents();
    refetchIndividualNpo();
    refetchNpoDonation();
  };

  useFocusEffect(
    useCallback(() => {
      if (state.user?.user?.id) onRefresh();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])
  );

  const onSearch = (search?: string) => {
    if (search && !isEmpty(search)) {
      setFilteredChatRooms(chats?.filter((it) => it.title.toUpperCase().includes(search.toUpperCase())));
    } else {
      setFilteredChatRooms(undefined);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounced = useMemo(() => debounce(() => onSearch(searchWatched), 400), [searchWatched]);

  useEffect(() => {
    debounced();
    return () => {
      debounced.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchWatched]);

  useEffect(() => {
    // Search only with ID in ALL chats
    if (roomId && !eventsPending && !individualNpoPending && !npoDonationsPending) {
      const findedRoom = chats?.find((it) => it.id === roomId);
      if (findedRoom !== null && findedRoom !== undefined && findedRoom.enabled) {
        switch (findedRoom.type) {
          case 'INDIVIDUALNPO': {
            router.navigate({
              pathname: '/messages/userNpo/[userId]',
              params: {
                userId: findedRoom.id,
                chatJson: JSON.stringify(findedRoom),
                individualThumbnail: findedRoom.individualThumb,
                npoThumbnail: findedRoom.nonProfitThumb
              }
            });
            break;
          }

          case 'VOLUNTEEREVENT': {
            router.navigate({
              pathname: '/messages/[volunteerEventId]',
              params: {
                volunteerEventId: findedRoom.id,
                chatJson: JSON.stringify(findedRoom)
              }
            });
            break;
          }

          case 'NPODONATIONS': {
            router.navigate({
              pathname: '/messages/npoDonations/[npoId]',
              params: {
                npoId: findedRoom.id,
                chatJson: JSON.stringify(findedRoom)
              }
            });
            break;
          }

          default:
            break;
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomId, eventsPending || individualNpoPending || npoDonationsPending]);

  if (!state.authReady) {
    return (
      <View style={styles.container}>
        <DotsLoader />
      </View>
    );
  }

  if (state.authReady && isBrowseOnlyUser) {
    router.replace('/unauthorized');
  }

  return (
    <View style={styles.flexOne}>
      <View style={styles.horizontalPadding}>
        {isSearching && (
          <Animated.View layout={LinearTransition} entering={SlideInRight} exiting={SlideOutRight}>
            <TextInputField
              name="search"
              control={control}
              placeholder={i18n.t('messages.search_message_placeholder')}
              leadingIcon={<Ionicons size={24} name="search" style={styles.iconColor} />}
              onSubmitEditing={(e) => {
                onSearch(e.nativeEvent.text);
              }}
              traillingIcon={
                <TouchableOpacity
                  onPress={() => {
                    setValue('search', undefined);
                    setIsSearching(false);
                  }}>
                  <Ionicons size={24} name="close" style={styles.iconColor} />
                </TouchableOpacity>
              }
              variant="rounded_with_shadow"
            />
          </Animated.View>
        )}
        {!isSearching && (
          <Animated.View layout={LinearTransition} style={styles.topBar} entering={SlideInRight} exiting={SlideOutLeft}>
            <TouchableOpacity onPress={() => setIsSearching(!isSearching)}>
              <Ionicons size={24} name="search" style={styles.iconColor} />
            </TouchableOpacity>
          </Animated.View>
        )}
      </View>

      <View style={styles.container}>
        {eventsPending || individualNpoPending || npoDonationsPending ? (
          <DotsLoader />
        ) : (
          <FlatList
            style={styles.flatList}
            data={filteredChatRooms ?? chats}
            keyExtractor={(item) => item.id}
            ItemSeparatorComponent={renderDivider}
            ListFooterComponent={renderDivider}
            showsVerticalScrollIndicator={false}
            refreshControl={
              <RefreshControl
                refreshing={eventsPending || individualNpoPending || npoDonationsPending}
                onRefresh={onRefresh}
                colors={[colors.primary]}
                tintColor={colors.primary}
              />
            }
            ListEmptyComponent={<ListEmptyComponent label={i18n.t('mix.n_a')} />}
            renderItem={(room) => {
              return (
                <ChatRoomCard
                  chatRoom={room.item}
                  onPress={() => {
                    if (room.item.enabled && room.item.type === 'VOLUNTEEREVENT')
                      router.navigate({
                        pathname: '/messages/[volunteerEventId]',
                        params: {
                          volunteerEventId: room.item.id,
                          chatJson: JSON.stringify(room.item)
                        }
                      });
                    if (room.item.enabled && room.item.type === 'INDIVIDUALNPO')
                      router.navigate({
                        pathname: '/messages/userNpo/[userId]',
                        params: {
                          userId: room.item.id,
                          chatJson: JSON.stringify(room.item),
                          individualThumbnail: room.item.individualThumb,
                          npoThumbnail: room.item.nonProfitThumb
                        }
                      });
                    if (room.item.enabled && room.item.type === 'NPODONATIONS')
                      router.navigate({
                        pathname: '/messages/npoDonations/[npoId]',
                        params: {
                          npoId: room.item.id,
                          chatJson: JSON.stringify(room.item)
                        }
                      });
                  }}
                />
              );
            }}
          />
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  flexOne: {
    flex: 1
  },

  horizontalPadding: {
    paddingHorizontal: 18
  },

  container: {
    flex: 1,
    flexDirection: 'column',
    paddingHorizontal: 18,
    justifyContent: 'center',
    alignItems: 'center'
  },

  topBar: {
    height: 38,
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center'
  },

  iconColor: {
    color: '#808080'
  },

  flatList: {
    width: '100%',
    height: '100%',
    marginTop: 25
  },

  divider: {
    marginBottom: 25,
    marginTop: 12
  }
});

export default MessagesList;
